import { useCallback, useEffect, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import { mapApiProcessingStatus } from "../api/mappers.js";
import type { ApiProcessingStatus } from "../api/types.js";
import type { AttachedDoc } from "../components/ChatComposer.js";
import { isAttachedDocProcessing } from "../lib/attached-doc-utils.js";
import type { DocumentStatus } from "../types.js";

interface UseAttachedDocPollingOptions {
  items: AttachedDoc[];
  setItems: Dispatch<SetStateAction<AttachedDoc[]>>;
  enabled: boolean;
  poll: (
    id: string,
    onStatus?: (status: ApiProcessingStatus | null | undefined) => void,
  ) => Promise<ApiProcessingStatus>;
  syncStatus: (id: string) => Promise<ApiProcessingStatus | null>;
}

export function useAttachedDocPolling({
  items,
  setItems,
  enabled,
  poll,
  syncStatus,
}: UseAttachedDocPollingOptions) {
  const pollingIds = useRef(new Set<string>());

  const updateItemStatus = useCallback(
    (id: string, status: DocumentStatus) => {
      setItems((prev) =>
        prev.map((doc) => (doc.id === id ? { ...doc, status } : doc)),
      );
    },
    [setItems],
  );

  const startPolling = useCallback(
    (id: string, status: DocumentStatus) => {
      if (!isAttachedDocProcessing(status)) return;
      if (pollingIds.current.has(id)) return;

      pollingIds.current.add(id);

      void (async () => {
        let resumeStatus: DocumentStatus | null = null;

        try {
          const result = await poll(id, (serverStatus) => {
            if (serverStatus) {
              updateItemStatus(id, mapApiProcessingStatus(serverStatus));
            }
          });
          updateItemStatus(id, mapApiProcessingStatus(result));
        } catch {
          try {
            const serverStatus = await syncStatus(id);
            if (serverStatus) {
              const mapped = mapApiProcessingStatus(serverStatus);
              updateItemStatus(id, mapped);
              if (isAttachedDocProcessing(mapped)) {
                resumeStatus = mapped;
              }
            }
          } catch {
            // Keep the current UI status rather than assuming failure.
          }
        } finally {
          pollingIds.current.delete(id);
        }

        if (resumeStatus) {
          startPolling(id, resumeStatus);
        }
      })();
    },
    [poll, syncStatus, updateItemStatus],
  );

  useEffect(() => {
    if (!enabled) return;

    for (const doc of items) {
      startPolling(doc.id, doc.status);
    }
  }, [enabled, items, startPolling]);

  return { startPolling };
}
